/**
 * DataSource — Abstraction layer for sensor data fetching.
 *
 * Strategies:
 *   1. mock    — Fake data from mockData.ts (default, no Pi)
 *   2. bridge  — Direct HTTP/SSE from Raspberry Pi FastAPI (habridge.ts)
 *   3. supabase — Realtime subscription from Supabase sensor_readings table
 *
 * The hook useClimateData now reads from DataSource instead of importing
 * mock/bridge directly — making the Pi integration a config switch.
 */

import type { SensorReading, DeviceStatus, DailySummary } from '../types';
import { supabase } from './supabase';
import { mockMode, fetchCurrentReading, fetchDevices, subscribeToStream } from './habridge';
import { getCurrentReading, getDeviceStatuses, getDailySummaries } from './mockData';
import { connectionLog } from './connectionLog';
import { enqueueMutation } from '../store/syncEngine';

// ── Source detection ──
export type DataSourceType = 'mock' | 'bridge' | 'supabase';

function detectSource(): DataSourceType {
  // Priority: supabase (cloud) > bridge (local Pi) > mock
  if (supabase) return 'supabase';
  if (!mockMode) return 'bridge';
  return 'mock';
}

export const dataSource: DataSourceType = detectSource();

let lastKnownReading: Record<string, SensorReading> = {};

// ── Current reading ──
export async function fetchCurrent(tentId = 'tent-1'): Promise<SensorReading | null> {
  if (dataSource === 'bridge' && tentId === 'tent-1') {
    const reading = await fetchCurrentReading();
    if (reading) {
      const res = { ...reading, tentId };
      lastKnownReading[tentId] = res;
      return res;
    }
    connectionLog.warn('DataSource', 'Bridge unreachable — last known fallback');
    return lastKnownReading[tentId] ?? { ...getCurrentReading(), receivedAt: new Date().toISOString(), tentId };
  }

  if (supabase) {
    try {
      const { data, error } = await supabase
        .from('sensor_readings')
        .select('*')
        .eq('tent_id', tentId)
        .order('timestamp', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!error && data) {
        const d = data as any;
        const res: SensorReading = { ...(d as SensorReading), tentId, receivedAt: d.received_at ?? d.receivedAt ?? d.timestamp };
        lastKnownReading[tentId] = res;
        return res;
      }
    } catch (e) {
      connectionLog.warn('DataSource', 'Supabase fetch hatası — last known fallback');
    }
    return lastKnownReading[tentId] ?? { ...getCurrentReading(), receivedAt: new Date().toISOString(), tentId };
  }

  return { ...getCurrentReading(), receivedAt: new Date().toISOString(), tentId };
}

// ── Device statuses ──
export async function fetchDeviceStatuses(): Promise<DeviceStatus[]> {
  if (dataSource === 'bridge') {
    const devices = await fetchDevices();
    if (devices) return devices;
  }

  if (dataSource === 'supabase' && supabase) {
    try {
      const { data, error } = await supabase
        .from('device_statuses')
        .select('*');
      if (!error && data) return data as DeviceStatus[];
    } catch {}
  }

  return getDeviceStatuses();
}

// ── Realtime subscriptions ──
export type UnsubscribeFn = () => void;

/**
 * Subscribe to realtime sensor readings.
 * Returns an unsubscribe function.
 */
export function subscribeToReadings(
  onReading: (r: SensorReading) => void,
  onError?: (msg: string) => void
): UnsubscribeFn {
  // Bridge mode: SSE stream from Pi
  if (dataSource === 'bridge') {
    connectionLog.info('DataSource', 'SSE stream (bridge) bağlanıyor...');
    return subscribeToStream(
      (r) => {
        connectionLog.success('DataSource', `SSE okuma: ${r.temperature}°C`);
        onReading(r);
      },
      () => {
        onError?.('SSE bağlantısı koptu');
        connectionLog.error('DataSource', 'SSE bağlantısı koptu');
      }
    );
  }

  // Supabase mode: Realtime channel
  if (dataSource === 'supabase' && supabase) {
    connectionLog.info('DataSource', 'Supabase Realtime kanalı açılıyor...');
    const sb = supabase;
    const channel = sb
      .channel('sensor-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'sensor_readings' },
        (payload) => {
          const raw = payload.new as any;
          const reading: SensorReading = { ...raw, tentId: raw.tent_id ?? raw.tentId, receivedAt: raw.received_at ?? raw.receivedAt ?? raw.timestamp };
          connectionLog.success('DataSource', `Realtime okuma: ${reading.temperature}°C`);
          onReading(reading);
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          connectionLog.success('DataSource', 'Supabase Realtime aktif');
        } else if (status === 'CHANNEL_ERROR') {
          onError?.('Supabase Realtime kanal hatası');
          connectionLog.error('DataSource', 'Realtime kanal hatası');
        }
      });

    return () => {
      sb.removeChannel(channel);
      connectionLog.info('DataSource', 'Supabase Realtime kapatıldı');
    };
  }

  // Mock mode: no subscription, noop
  connectionLog.info('DataSource', 'Mock mod — realtime yok');
  return () => {};
}

/**
 * Subscribe to device status changes (Supabase only).
 */
export function subscribeToDeviceChanges(
  onUpdate: (d: DeviceStatus) => void
): UnsubscribeFn {
  if (dataSource !== 'supabase' || !supabase) return () => {};

  const sb = supabase;
  const channel = sb
    .channel('device-realtime')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'device_statuses' },
      (payload) => {
        onUpdate(payload.new as DeviceStatus);
      }
    )
    .subscribe();

  return () => {
    sb.removeChannel(channel);
  };
}

// ── Sensor history — son N saat için okumalar ──
export async function fetchReadingsHistory(hours: number, tentId = 'tent-1'): Promise<SensorReading[]> {
  if (supabase) {
    try {
      const from = new Date(Date.now() - hours * 3600 * 1000).toISOString();
      let query = supabase
        .from('sensor_readings')
        .select('timestamp, temperature, humidity, co2, tent_id')
        .gte('timestamp', from)
        .order('timestamp', { ascending: true });
      query = query.eq('tent_id', tentId);
      const { data, error } = await query;
      if (!error && data && data.length > 0) {
        return data.map((r: any) => ({
          timestamp:   r.timestamp as string,
          receivedAt:  r.timestamp as string,
          temperature: r.temperature as number,
          humidity:    r.humidity as number,
          co2:         r.co2 as number,
          tentId:      r.tent_id as string,
        }));
      }
    } catch {}
  }

  return generateMockHistory(hours);
}

function generateMockHistory(hours: number): SensorReading[] {
  const now = Date.now();
  const intervalMs = hours <= 1 ? 5 * 60_000 : hours <= 6 ? 15 * 60_000 : 60 * 60_000;
  const readings: SensorReading[] = [];
  let temp = 18.0, hum = 90, co2 = 650;
  for (let t = now - hours * 3600_000; t <= now; t += intervalMs) {
    temp = Math.round((temp + (Math.random() - 0.5) * 0.4) * 10) / 10;
    hum  = Math.round(Math.min(97, Math.max(80, hum  + (Math.random() - 0.5) * 2)));
    co2  = Math.round(Math.min(1100, Math.max(400, co2 + (Math.random() - 0.5) * 60)));
    const ts = new Date(t).toISOString();
    readings.push({ timestamp: ts, receivedAt: ts, temperature: temp, humidity: hum, co2 });
  }
  return readings;
}

// ── Bugünün okumaları (intraday chart için) ──
export async function fetchTodayReadings(tentId = 'tent-1'): Promise<SensorReading[]> {
  const todayStart = new Date(Date.now() - 24 * 3600 * 1000);

  // Supabase: bugün gece yarısından itibaren tüm okumalar
  if (supabase) {
    try {
      const { data, error } = await supabase
        .from('sensor_readings')
        .select('timestamp, temperature, humidity, co2')
        .eq('tent_id', tentId)
        .gte('timestamp', todayStart.toISOString())
        .order('timestamp', { ascending: true });
      if (!error && data && data.length > 0) {
        return data.map((r: any) => ({
          timestamp:   r.timestamp as string,
          receivedAt:  r.timestamp as string,
          temperature: r.temperature as number,
          humidity:    r.humidity as number,
          co2:         r.co2 as number,
        }));
      }
    } catch {}
  }

  return generateMockHistory(24);
}

// ── Daily summaries from sensor_daily_summaries view ──
export async function fetchDailySummaries(days = 30, tentId?: string): Promise<DailySummary[]> {
  if (supabase) {
    try {
      const from = new Date();
      from.setDate(from.getDate() - days + 1);
      from.setHours(0, 0, 0, 0);
      let query = supabase
        .from('sensor_daily_summaries')
        .select('*')
        .gte('date', from.toISOString().split('T')[0])
        .order('date', { ascending: false });
      if (tentId) query = query.eq('tent_id', tentId);
      const { data, error } = await query;
      if (!error && data && data.length > 0) {
        return data.map((row, i) => ({
          date:           row.date as string,
          dayNumber:      data.length - i,
          avgTemp:        row.avg_temp   as number,
          minTemp:        row.min_temp   as number,
          maxTemp:        row.max_temp   as number,
          avgHumidity:    row.avg_humidity as number,
          minHumidity:    row.min_humidity as number,
          maxHumidity:    row.max_humidity as number,
          avgCo2:         row.avg_co2    as number,
          maxCo2:         row.max_co2    as number,
          healthScore:    0,
          alerts:         0,
          faeActivations: 0,
        }));
      }
    } catch {}
  }
  return getDailySummaries();
}

// ── Day hourly detail ──
import type { DayHourlyDetail } from '../types';

export async function fetchDayHourlyDetail(date: string, dayNumber: number): Promise<DayHourlyDetail | null> {
  if (supabase) {
    try {
      const dayStart = `${date}T00:00:00`;
      const dayEnd   = `${date}T23:59:59`;
      const [readingsRes, alertsRes] = await Promise.all([
        supabase
          .from('sensor_readings')
          .select('timestamp, temperature, humidity, co2')
          .gte('timestamp', dayStart)
          .lte('timestamp', dayEnd)
          .order('timestamp', { ascending: true }),
        supabase
          .from('alerts')
          .select('*')
          .gte('timestamp', dayStart)
          .lte('timestamp', dayEnd)
          .order('timestamp', { ascending: true }),
      ]);
      if (!readingsRes.error && readingsRes.data && readingsRes.data.length > 0) {
        const readings = readingsRes.data.map((r: any) => ({
          timestamp: new Date(r.timestamp).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
          temperature: r.temperature as number,
          humidity: r.humidity as number,
          co2: r.co2 as number,
        }));
        const alerts = (!alertsRes.error && alertsRes.data)
          ? alertsRes.data.map((a: any) => ({
              id: a.id as string,
              timestamp: a.timestamp as string,
              severity: a.severity as import('../types').AlertEvent['severity'],
              metric: a.metric as import('../types').AlertEvent['metric'],
              message: a.message as string,
              value: a.value as number,
              threshold: a.threshold as number,
              resolved: a.resolved as boolean,
            }))
          : [];
        return { date, dayNumber, readings, alerts };
      }
    } catch {}
  }
  const { getDayHourlyDetail } = await import('./mockData');
  return getDayHourlyDetail(date);
}

// ── Alerts ──
import type { AlertEvent } from '../types';

export async function fetchAlerts(days = 7): Promise<AlertEvent[]> {
  if (supabase) {
    try {
      const from = new Date();
      from.setDate(from.getDate() - days);
      const { data, error } = await supabase
        .from('alerts')
        .select('*')
        .gte('timestamp', from.toISOString())
        .order('timestamp', { ascending: false });
      if (!error && data && data.length > 0) {
        return data.map(row => ({
          id:        row.id as string,
          timestamp: row.timestamp as string,
          severity:  row.severity as AlertEvent['severity'],
          metric:    row.metric as AlertEvent['metric'],
          message:   row.message as string,
          value:     row.value as number,
          threshold: row.threshold as number,
          resolved:  row.resolved as boolean,
        }));
      }
    } catch {}
  }
  const { getRecentAlerts } = await import('./mockData');
  return getRecentAlerts();
}

export async function saveAlert(alert: AlertEvent): Promise<void> {
  if (!supabase) return;
  const payload = {
    id:        alert.id,
    timestamp: alert.timestamp,
    severity:  alert.severity,
    metric:    alert.metric,
    message:   alert.message,
    value:     alert.value,
    threshold: alert.threshold,
    resolved:  alert.resolved,
  };
  await enqueueMutation({ type: 'saveAlert', payload });
}

// ── Device Events ──
import type { DeviceEvent } from '../types';

export async function fetchDeviceEvents(days = 7): Promise<DeviceEvent[]> {
  if (supabase) {
    try {
      const from = new Date();
      from.setDate(from.getDate() - days);
      const { data, error } = await supabase
        .from('device_events')
        .select('*')
        .gte('started_at', from.toISOString())
        .order('started_at', { ascending: false });
      if (!error && data && data.length > 0) {
        return data.map((r: any) => ({
          id:              r.id as string,
          deviceId:        r.device_id as string,
          deviceName:      r.device_name as string,
          deviceType:      r.device_type as string,
          reason:          r.reason as string,
          startedAt:       r.started_at as string,
          endedAt:         r.ended_at as string | undefined,
          durationMinutes: r.duration_minutes as number | undefined,
        }));
      }
    } catch {}
  }
  const { getDeviceEvents } = await import('./mockData');
  return getDeviceEvents();
}

export async function saveDeviceEvent(event: DeviceEvent): Promise<void> {
  if (!supabase) return;
  const payload = {
    id:               event.id,
    device_id:        event.deviceId,
    device_name:      event.deviceName,
    device_type:      event.deviceType,
    reason:           event.reason,
    started_at:       event.startedAt,
    ended_at:         event.endedAt ?? null,
    duration_minutes: event.durationMinutes ?? null,
  };
  await enqueueMutation({ type: 'saveDeviceEvent', payload });
}

// ── Connection status helper ──
export function getSourceLabel(): string {
  switch (dataSource) {
    case 'bridge':  return '🟢 Pi Bağlı';
    case 'supabase': return '🔵 Supabase';
    case 'mock':     return '🟡 Mock';
  }
}

// ── Photo Logs ──
import type { PhotoLog } from '../types';

export async function fetchPhotoLogs(tentId = 'tent-1', limit = 10): Promise<PhotoLog[]> {
  if (supabase) {
    try {
      const { data, error } = await supabase
        .from('photo_logs')
        .select('*')
        .eq('tent_id', tentId)
        .order('taken_at', { ascending: false })
        .limit(limit);
      if (!error && data) {
        return data.map((r: any) => ({
          id: r.id,
          bagId: r.bag_id,
          date: r.taken_at,
          description: r.note || '',
          stage: 'incubation', // default or derive from somewhere
          imageEmoji: '📷',
          imageUrl: r.url,
          captureSource: 'timelapse'
        }));
      }
    } catch (e) {
      connectionLog.error('DataSource', `fetchPhotoLogs hatası: ${e}`);
    }
  }
  return [];
}
